/**
 * Best-effort URL extraction from a chunk-load error.
 *
 * Sources, in priority order:
 * 1. `error.request` — webpack 5's `ChunkLoadError` exposes the failing URL
 *    here (https://github.com/webpack/webpack/blob/main/lib/runtime/LoadScriptRuntimeModule.js)
 * 2. URL parsed from `error.message` — webpack messages of the form
 *    "Loading chunk N failed. (timeout: https://…)" carry the URL inline.
 * 3. URL parsed from `error.stack` — last-resort fallback.
 *
 * Returns `undefined` if no URL can be recovered.
 */ export const extractChunkUrlFromError = (error)=>{
    const err = error;
    if (typeof (err === null || err === void 0 ? void 0 : err.request) === 'string' && err.request.length > 0) {
        return err.request;
    }
    const messageMatch = typeof (err === null || err === void 0 ? void 0 : err.message) === 'string' ? err.message.match(/https?:\/\/[^\s)]+/) : null;
    if (messageMatch) {
        return messageMatch[0];
    }
    const stackMatch = typeof (err === null || err === void 0 ? void 0 : err.stack) === 'string' ? err.stack.match(/https?:\/\/[^\s)]+\.(?:js|css|mjs)/) : null;
    return stackMatch === null || stackMatch === void 0 ? void 0 : stackMatch[0];
};
/**
 * Diagnostic snapshot for a chunk-load failure. Designed to be merged into
 * the `context` passed to `logger.error`, so Datadog RUM `addError` (which
 * the logger already wraps) carries these attributes for filterable
 * queries — letting us distinguish stale-deploy / SW-intercepted /
 * disk-cache-corruption / offline failure modes from logs alone.
 *
 * SYNCHRONOUS by design — every value is read from APIs that resolve
 * inline (no fetch, no probe, no I/O). This guarantees the helper cannot
 * extend the user-visible delay before `reloadAfterChunkError` fires. An
 * earlier draft included a `fetch(url, { method: 'HEAD' })` to capture
 * the HTTP status code, but that probe — even with a 3s AbortController
 * cap — was awaited on the critical reload path in `lazyWithRetry`,
 * delaying the user's recovery by up to 3 seconds. The HTTP status field
 * was the only async-derivable attribute; if we want it later, we should
 * fire it from a separate non-blocking surface.
 *
 * Fields:
 *   - chunkUrl: extracted URL of the failing chunk (or '')
 *   - chunkTransferSize: bytes transferred per Navigation Timing API
 *     (0 = served from disk cache; -1 = no entry available)
 *   - chunkSwControlled: whether a SW was intercepting at error time
 *   - chunkOnline: navigator.onLine at error time
 *   - chunkUserAgent: UA string for browser-cohort analysis
 *
 * Wrapped in try/catch so an exotic environment (e.g. iframe with no
 * `performance` global) cannot crash the error path. Telemetry never
 * throws.
 */ export const captureChunkErrorTelemetry = (error)=>{
    try {
        var _extractChunkUrlFromError;
        const url = (_extractChunkUrlFromError = extractChunkUrlFromError(error)) !== null && _extractChunkUrlFromError !== void 0 ? _extractChunkUrlFromError : '';
        const swControlled = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.controller != null;
        const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        let transferSize = -1;
        if (url && typeof performance !== 'undefined') {
            try {
                const entries = performance.getEntriesByName(url);
                const last = entries[entries.length - 1];
                if (last) {
                    transferSize = last.transferSize;
                }
            } catch (unused) {
            /* noop */ }
        }
        return {
            chunkUrl: url,
            chunkTransferSize: transferSize,
            chunkSwControlled: swControlled,
            chunkOnline: online,
            chunkUserAgent: userAgent
        };
    } catch (unused) {
        return {};
    }
};
export const isChunkLoadError = (error)=>{
    const err = error;
    const name = err === null || err === void 0 ? void 0 : err.name;
    const message = err === null || err === void 0 ? void 0 : err.message;
    const messageIsString = typeof message === 'string';
    return name === 'ChunkLoadError' || messageIsString && message.includes('Loading chunk') || messageIsString && message.includes('Loading CSS chunk');
};
const RELOAD_KEY = 'chunk-error-reloaded-at';
const RELOAD_DEBOUNCE_MS = 30000;
// localStorage key — survives the cross-tab boundary that sessionStorage
// can't, so we can correlate the reload trigger with the next page's boot.
const RELOAD_OUTCOME_KEY = 'chunk-error-reload-outcome';
// LogRocket re-initializes on the next page, so any track() call we issue
// during boot lands in the NEW session — giving us replay coverage of the
// outcome. The previous (failing) session is destroyed by window.location
// .replace, which is exactly why we couldn't see it before.
const RELOAD_OUTCOME_TRACK_EVENT = 'chunk-error-reload-outcome';
export const wasRecentlyReloadedForChunkError = (now = Date.now())=>{
    let lastReloadAt = 0;
    try {
        var _window_sessionStorage_getItem;
        lastReloadAt = Number((_window_sessionStorage_getItem = window.sessionStorage.getItem(RELOAD_KEY)) !== null && _window_sessionStorage_getItem !== void 0 ? _window_sessionStorage_getItem : 0);
    } catch (unused) {
        lastReloadAt = 0;
    }
    return now - lastReloadAt < RELOAD_DEBOUNCE_MS;
};
export const markChunkErrorReloadAttempt = (now = Date.now())=>{
    try {
        window.sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch (unused) {
    /* noop */ }
};
export const isDevelopmentBuild = ()=>process.env.NODE_ENV === 'development';
const getPageRefreshedFlag = ()=>{
    try {
        return JSON.parse(window.localStorage.getItem('page-refreshed') || 'false');
    } catch (unused) {
        return false;
    }
};
/**
 * Whether a chunk-load failure should trigger an automatic page reload.
 *
 * Production: one reload per boot (`page-refreshed`) with a 30s debounce.
 * Development: never reload — HMR / Fast Refresh should apply edits in-place;
 * transient chunk 404s during rebuild are handled by `retryImportAfterChunkError`.
 */ export const shouldAutoReloadForChunkError = (now = Date.now())=>{
    if (isDevelopmentBuild()) {
        return false;
    }
    return !wasRecentlyReloadedForChunkError(now) && !getPageRefreshedFlag();
};
const DEV_CHUNK_RETRY_ATTEMPTS = 4;
const DEV_CHUNK_RETRY_DELAY_MS = 250;
const DEV_FALLBACK_RELOAD_KEY = 'dev-chunk-fallback-reload-at';
const DEV_FALLBACK_RELOAD_MS = 15000;
/** Retry a dynamic import while rspack HMR rebuilds invalidate async chunks. */ export const retryImportAfterChunkError = async (importFn)=>{
    let lastError;
    for(let attempt = 0; attempt < DEV_CHUNK_RETRY_ATTEMPTS; attempt += 1){
        if (attempt > 0) {
            await new Promise((resolve)=>{
                setTimeout(resolve, DEV_CHUNK_RETRY_DELAY_MS);
            });
        }
        try {
            return await importFn();
        } catch (err) {
            lastError = err;
            if (!isChunkLoadError(err)) {
                throw err;
            }
        }
    }
    throw lastError;
};
/** One debounced full reload in dev when import retries are exhausted. */ export const shouldDevFallbackReload = (now = Date.now())=>{
    if (!isDevelopmentBuild()) {
        return false;
    }
    try {
        var _window_sessionStorage_getItem;
        const last = Number((_window_sessionStorage_getItem = window.sessionStorage.getItem(DEV_FALLBACK_RELOAD_KEY)) !== null && _window_sessionStorage_getItem !== void 0 ? _window_sessionStorage_getItem : 0);
        return now - last >= DEV_FALLBACK_RELOAD_MS;
    } catch (unused) {
        return true;
    }
};
export const markDevFallbackReload = (now = Date.now())=>{
    try {
        window.sessionStorage.setItem(DEV_FALLBACK_RELOAD_KEY, String(now));
    } catch (unused) {
    /* noop */ }
};
/** Record a prod chunk reload attempt (does not set the prod one-shot latch in dev). */ export const markAutoReloadForChunkError = (now = Date.now())=>{
    if (isDevelopmentBuild()) {
        return;
    }
    markChunkErrorReloadAttempt(now);
    try {
        window.localStorage.setItem('page-refreshed', 'true');
    } catch (unused) {
    /* noop */ }
};
const TERMINAL_LOGGED_KEY = 'chunk-error-terminal-logged';
/**
 * Dedup for the terminal chunk-error logs. The error-boundary fallback (App.tsx)
 * re-renders while stuck in the error state and re-logs on every render —
 * production showed ~8 log events per crashing session for a single failed
 * chunk. Emit once per (session, scope, chunkUrl) so logged volume reflects
 * real incidents, not React re-renders.
 *
 * `scope` namespaces the dedup per call-site (e.g. the boundary fallback vs
 * lazyWithRetry). These emit DIFFERENT signatures ('...already reloaded' vs
 * '...giving up') and must be counted independently — a shared list would let
 * whichever fires first suppress the other's distinct signature and under-count
 * it on dashboards/alerts.
 *
 * Keyed by scope+chunkUrl in sessionStorage: a genuinely different chunk failure
 * in the same session still logs, and a new session (new tab / fresh boot)
 * starts clean. Never suppresses a distinct failure. Best-effort — if storage is
 * unavailable it returns true so we err on the side of logging.
 */ export const shouldLogTerminalChunkError = (chunkUrl, scope)=>{
    const key = `${scope}:${chunkUrl || '__no_url__'}`;
    try {
        const raw = window.sessionStorage.getItem(TERMINAL_LOGGED_KEY);
        const logged = raw ? JSON.parse(raw) : [];
        if (logged.includes(key)) {
            return false;
        }
        logged.push(key);
        window.sessionStorage.setItem(TERMINAL_LOGGED_KEY, JSON.stringify(logged));
        return true;
    } catch (unused) {
        return true;
    }
};
const recordReloadOutcomeAttempt = (now, errorContext)=>{
    try {
        const record = {
            startedAt: now,
            fromUrl: window.location.href,
            errorName: errorContext === null || errorContext === void 0 ? void 0 : errorContext.name,
            errorMessage: errorContext === null || errorContext === void 0 ? void 0 : errorContext.message
        };
        window.localStorage.setItem(RELOAD_OUTCOME_KEY, JSON.stringify(record));
    } catch (unused) {
    /* noop — quota-exceeded / storage-disabled is best-effort only */ }
};
export const reloadAfterChunkError = (now = Date.now(), errorContext)=>{
    // Persist BEFORE replacing the URL so the next page's boot can read it.
    // The page about to be destroyed wouldn't get to fire any LogRocket
    // event itself (the SDK is mid-flush when location.replace runs), so
    // localStorage is the only handoff to the recovered (or failing-again)
    // page.
    recordReloadOutcomeAttempt(now, errorContext);
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('__cb');
        url.searchParams.set('__cb', String(now));
        window.location.replace(url.toString());
    } catch (unused) {
        window.location.reload();
    }
};
/**
 * Reads (and clears) the localStorage outcome record left by
 * `reloadAfterChunkError`. If a record is present, the current page's
 * boot is the FIRST observable event after a chunk-error reload — we
 * track it via LogRocket so the recovery (or repeat-failure) is
 * captured in the new session.
 *
 * Called once on App mount, alongside `stripCacheBusterParam`. The
 * outcome is inferred from whether the page got far enough to call this:
 *   - If the page boots successfully → call sees the record, fires
 *     `outcome: 'recovered'`. Recovery confirmed.
 *   - If the page also fails to boot → this call never runs, the record
 *     persists, and the NEXT successful boot will see it. The age
 *     (`now - startedAt`) tells us how long the user was stuck.
 *
 * Wraps everything in try/catch because LogRocket might not be loaded,
 * localStorage might be disabled, or the JSON might be corrupt.
 */ export const reportChunkErrorReloadOutcome = (now = Date.now())=>{
    try {
        var _ref;
        const raw = window.localStorage.getItem(RELOAD_OUTCOME_KEY);
        if (!raw) {
            return;
        }
        window.localStorage.removeItem(RELOAD_OUTCOME_KEY);
        const record = JSON.parse(raw);
        const elapsedMs = now - ((_ref = record === null || record === void 0 ? void 0 : record.startedAt) !== null && _ref !== void 0 ? _ref : now);
        // Fire-and-forget — LogRocket is split into its own chunk (see
        // `src/vendor/log-rocket/index.ts`). Keeping it dynamic here avoids
        // pulling the SDK into the shell bundle.
        import(/* webpackChunkName: "logrocket" */ 'logrocket').then(({ default: LogRocket })=>{
            var _ref, _ref1, _ref2;
            LogRocket.track(RELOAD_OUTCOME_TRACK_EVENT, {
                outcome: 'recovered',
                elapsedMs,
                fromUrl: (_ref = record === null || record === void 0 ? void 0 : record.fromUrl) !== null && _ref !== void 0 ? _ref : '',
                errorName: (_ref1 = record === null || record === void 0 ? void 0 : record.errorName) !== null && _ref1 !== void 0 ? _ref1 : '',
                errorMessage: (_ref2 = record === null || record === void 0 ? void 0 : record.errorMessage) !== null && _ref2 !== void 0 ? _ref2 : '',
                currentUrl: window.location.href
            });
        }).catch(()=>{
        /* noop — LogRocket may be blocked or skipped on this build */ });
    } catch (unused) {
    /* noop */ }
};
/**
 * Removes the chunk-error cache-buster (`?__cb=...`) from the URL bar without
 * triggering a navigation. Called once on App mount so that a successful
 * recovery doesn't leave a polluting query param visible to the user or
 * persist into bookmarks.
 *
 * Safe to call regardless of whether the param is present — no-op if absent.
 * Wraps URL construction and history.replaceState in try/catch because
 * exotic origins or corrupted history state should not crash boot.
 */ export const stripCacheBusterParam = ()=>{
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('__cb')) {
            url.searchParams.delete('__cb');
            window.history.replaceState(window.history.state, '', url.toString());
        }
    } catch (unused) {
    /* noop */ }
};
